"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
const sharedData = {
  email: "info@cynthyleehealthcare.com",
  phone: "01273 987001",
  addresses: [{
    title: "Brighton Office",
    address: "Ebenezer Church, Richmond Parade, Brighton, BN2 9AA"
  }]
};
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex min-h-[320px] flex-col items-center justify-center rounded-3xl bg-teal-50 p-10 text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-100 text-teal-700">
          <Mail className="h-8 w-8" />
        </div>

        <h3 className="text-2xl font-bold text-slate-900">
          Inquiry Received
        </h3>

        <p className="mt-4 max-w-md text-slate-600">
          {successMessage}
        </p>

        <Button onClick={resetForm} className="mt-6 rounded-lg bg-teal-700 hover:bg-teal-800">
          Send Another Message
        </Button>
      </div>;
  }
  const inputStyles = "h-12 rounded-xl border-slate-200 bg-white";
  return <Form onSubmit={handleSubmit} className="space-y-6">

      <div className="grid gap-5 md:grid-cols-2">

        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name *</FormLabel>

              <FormControl>
                <Input placeholder="Jane Smith" className={inputStyles} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address *</FormLabel>

              <FormControl>
                <Input type="email" placeholder="jane@example.com" className={inputStyles} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

      </div>

      <div className="grid gap-5 md:grid-cols-2">

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>

              <FormControl>
                <Input type="tel" placeholder="01273 000000" className={inputStyles} {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="care_type" render={({
        field
      }) => <FormItem>
              <FormLabel>Care Service</FormLabel>

              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={inputStyles}>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  {["Personal Care", "Companionship Care", "Live-in Care", "Respite Care", "Overnight Care"].map(option => <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>} />

      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>How can we help?</FormLabel>

            <FormControl>
              <Textarea rows={5} placeholder="Tell us about your care needs..." className="rounded-2xl border-slate-200" {...field} />
            </FormControl>

            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-lg bg-teal-700 text-base font-semibold text-white hover:bg-teal-800">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending...
          </> : <>
            Submit Care Request
            <ArrowRight className="ml-2 h-5 w-5" />
          </>}
      </Button>

    </Form>;
}
export default function StartAProject() {
  return <section id="start-your-care-journey" className="bg-slate-50 px-4 py-16 md:px-6 lg:py-24">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">

        {}
        <div>

          <div className="max-w-lg">

            <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-700">
              Contact Us
            </p>

            <h2 className="mt-4 text-4xl font-bold tracking-tight text-slate-900 md:text-5xl">
              Start Your Care Journey
            </h2>

            <p className="mt-5 text-lg leading-8 text-slate-600">
              Speak with our compassionate team for personalised care
              support across Brighton & Hove.
            </p>

          </div>

          <div className="mt-10 space-y-5">

            <Link to={`mailto:${sharedData.email}`} className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100 transition hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                <Mail className="h-5 w-5" />
              </div>

              <div>
                <p className="text-sm text-slate-500">
                  Email
                </p>

                <p className="font-semibold text-slate-900">
                  {sharedData.email}
                </p>
              </div>
            </Link>

            <Link to={`tel:${sharedData.phone.replace(/\s+/g, "")}`} className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100 transition hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                <Phone className="h-5 w-5" />
              </div>

              <div>
                <p className="text-sm text-slate-500">
                  Phone
                </p>

                <p className="font-semibold text-slate-900">
                  {sharedData.phone}
                </p>
              </div>
            </Link>

            {sharedData.addresses.map((loc, i) => <div key={i} className="flex gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                  <MapPin className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-sm text-slate-500">
                    {loc.title}
                  </p>

                  <p className="mt-1 font-semibold leading-7 text-slate-900">
                    {loc.address}
                  </p>
                </div>
              </div>)}

          </div>
        </div>

        {}
        <FormProvider className="bg-white p-6 shadow-xl ring-1 ring-slate-100 md:p-8" formId="care-journey-contact" sectionName="StartAProject" defaultValues={{
        name: "",
        email: "",
        phone: "",
        care_type: "",
        message: ""
      }} successMessage="Thank you for contacting Cynthylee Health Care. Our team will be in touch shortly.">
          <ProjectForm />
        </FormProvider>

      </div>
    </section>;
}